import Logo from '@/common/components/atom/logo';
import ChevronIcon from '@/common/components/icons/chevron';
import HumbuggerMenuIcon from '@/common/components/icons/humbuggerMenu';
import classNames from '@/common/utils/classNames';
import doctorGroupImage from '@/modules/search/components/consultBanner/doctor-group.webp';
import Link from 'next/link';
import { ReactNode } from 'react';
import type { CorporatePlan } from '../types';
import { OrganizationLogo } from './organizationLogo';

/** Where the employee's online visits start: the online-visit doctors. */
export const CORPORATE_ONLINE_VISIT_HREF = '/s/?turn_type=consult';

/**
 * Mobile header for a signed-in employee: the site's own partner co-branding (Paziresh24 logo, dashed
 * divider, partner logo — `customize.showPartnerLogoInPrimaryPlace`), with the organization as partner.
 * Used by the design preview; the live header adds only `CorporateHeaderMark` (see live.tsx).
 */
export const CorporateHeader = ({ plan, initial }: { plan: CorporatePlan; initial: string }) => (
  <header className="flex h-14 min-h-14 items-center justify-between border-b border-slate-100 bg-white px-3 text-slate-700 shadow-sm">
    <div className="flex items-center gap-2">
      <span className="grid h-8 w-8 place-items-center">
        <HumbuggerMenuIcon />
      </span>
      <Logo fontSize="sm" width={32} height={32} />
      <hr className="h-8 rounded-full border border-dashed border-slate-300" />
      <OrganizationLogo organization={plan.organization} height={26} />
    </div>
    <span className="grid h-9 w-9 place-items-center rounded-full bg-slate-100 text-sm font-bold text-slate-600">{initial}</span>
  </header>
);

const thousands = (toman: number) => `${Math.round(toman / 1000)} هزار تومان`;

/** The benefit in two weights: what (bold) and from whom. */
const benefit = (plan: CorporatePlan): [string, string] => {
  const name = plan.organization.name;
  switch (plan.kind) {
    case 'budget':
      return ['ویزیت آنلاین رایگان', `از طرف ${name}`];
    case 'cap':
      return [`تا ${thousands(plan.capToman)}`, `از هر ویزیت با ${name}`];
    default:
      return plan.remaining > 0
        ? [`${plan.remaining} ویزیت آنلاین رایگان`, `از طرف ${name}`]
        : ['سهمیهٔ رایگان این دوره تمام شده', `تمدید ${plan.renewsOn}`];
  }
};

/** One sentence for the welcome card on top of the results. */
const welcomeTitle = (plan: CorporatePlan) => {
  const name = plan.organization.name;
  switch (plan.kind) {
    case 'budget':
      return `ویزیت آنلاین رایگان از طرف ${name}`;
    case 'cap':
      return `هزینهٔ هر ویزیت تا ${thousands(plan.capToman)} با ${name}`;
    default:
      return plan.remaining > 0
        ? `${plan.remaining} ویزیت آنلاین رایگان از طرف ${name}`
        : `سهمیهٔ رایگان شما ${plan.renewsOn} تمدید می‌شود`;
  }
};

/** A link when it leads somewhere, a button when the preview drives it. */
const Action = ({ href, onClick, className, children }: { href?: string; onClick?: () => void; className: string; children: ReactNode }) =>
  onClick || !href ? (
    <button type="button" onClick={onClick} className={className}>
      {children}
    </button>
  ) : (
    <Link href={href} prefetch={false} className={className}>
      {children}
    </Link>
  );

const ShieldCheckIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M12 3l7 3v5c0 4.5-3 8.3-7 10-4-1.7-7-5.5-7-10V6z" />
    <path d="M9 12l2 2 4-4" />
  </svg>
);

/**
 * Home, right under the search box: one small green pill — what the employee has, and from whom.
 * No organization logo here: it is already in the header.
 */
export const CorporateHomeNote = ({
  plan,
  href = CORPORATE_ONLINE_VISIT_HREF,
  onClick,
}: {
  plan: CorporatePlan;
  href?: string;
  onClick?: () => void;
}) => {
  const [what, from] = benefit(plan);
  return (
    <Action
      href={href}
      onClick={onClick}
      className="flex h-10 items-center gap-2 self-center whitespace-nowrap rounded-full border border-green-100 bg-green-50 pe-3.5 ps-4 text-sm text-green-700"
    >
      <span className="text-green-600">
        <ShieldCheckIcon />
      </span>
      <span className="text-[13px]">
        <b className="font-bold text-green-800">{what}</b> {from}
      </span>
      <ChevronIcon dir="left" className="text-green-600" aria-hidden="true" />
    </Action>
  );
};

/**
 * Top of the search results for an employee, in the shape of the site's own online-visit banner
 * (`ConsultBanner`: greeting, one line, doctor-group artwork, «گفت‌وگو با پزشک»).
 */
export const CorporateWelcome = ({
  plan,
  firstName,
  href = CORPORATE_ONLINE_VISIT_HREF,
  onChat,
  showAction = true,
  className,
}: {
  plan: CorporatePlan;
  firstName: string;
  href?: string;
  onChat?: () => void;
  /** `false`: the patient is already on the online-visit list. */
  showAction?: boolean;
  className?: string;
}) => (
  <section aria-label="ویزیت آنلاین سازمانی" className={classNames('flex flex-col gap-3.5 rounded-lg bg-white p-4', className)}>
    <div className="flex flex-col gap-0.5">
      <span className="text-[13px] leading-[22px] text-slate-500">{firstName ? `${firstName} عزیز، سلام` : 'سلام'}</span>
      <h2 className="text-base font-bold leading-7 text-slate-900">{welcomeTitle(plan)}</h2>
    </div>
    <div className="flex items-center justify-between gap-3">
      <img src={doctorGroupImage.src} alt="" width={132} height={40} decoding="async" className="shrink-0" />
      {showAction && (
        <Action
          href={href}
          onClick={onChat}
          className="flex h-10 items-center gap-2.5 whitespace-nowrap rounded-lg bg-primary px-3.5 text-[13px] font-bold text-white"
        >
          گفت‌وگو با پزشک
          <ChevronIcon dir="left" aria-hidden="true" />
        </Action>
      )}
    </div>
  </section>
);
